import { createReference, MedplumClient } from '@medplum/core'
import {
  parseHHMM,
  toIsoLocalZ,
  utcDayIso,
  updateDateTime24,
  isMultipleDayRange,
  buildDailyPairs,
} from './utils'
import {
  Resource,
  Schedule,
  Slot,
  BundleEntry,
  BundleEntryResponse,
  Bundle,
} from '@medplum/fhirtypes'
import { showNotification } from '@mantine/notifications'
import { logError } from '@/utils/errorUtils'
import type { ApiClient } from '@behuman.corp/contracts'
import { VISIT_REASON_SOURCE_STAFF } from '@/model/middleware-types'

export type DayKey = 'Sun' | 'Mon' | 'Tue' | 'Wed' | 'Thu' | 'Fri' | 'Sat'
export type Draft = Record<DayKey, { start: string; end: string } | undefined>
export type SlotStatus =
  | 'free'
  | 'busy-unavailable'
  | 'busy-tentative'
  | 'busy'
  | 'entered-in-error'

function createEntry<T extends Resource>(resource: T): BundleEntry<T> {
  return {
    resource,
    request: {
      url: resource.resourceType,
      method: 'POST',
    },
  }
}

async function deleteSlotsByIds(
  medplum: MedplumClient,
  ids: string[],
  chunkSize = 200,
): Promise<void> {
  for (let i = 0; i < ids.length; i += chunkSize) {
    const slice = ids.slice(i, i + chunkSize)
    const bundle: Bundle = {
      resourceType: 'Bundle',
      type: 'transaction', // use 'batch' if you don't need atomicity per chunk
      entry: slice.map((id) => ({
        request: { method: 'DELETE', url: `Slot/${encodeURIComponent(id)}` },
      })),
    }
    await medplum.executeBatch(bundle)
  }
}
function createTimeSlots(
  periodStart: string | Date,
  periodEnd: string | Date,
): Array<{ slotStart: string; slotEnd: string }> {
  const slots = []
  let current = new Date(periodStart)
  const end = new Date(periodEnd)

  while (current < end) {
    const slotStart = new Date(current)
    const slotEnd = new Date(current.getTime() + 30 * 60 * 1000) // +30 mins

    slots.push({
      slotStart: slotStart.toISOString(),
      slotEnd: slotEnd.toISOString(),
    })

    current = slotEnd
  }

  return slots
}

function buildFreeSlots(
  startDate: string | Date,
  endDate: string | Date,
  scheduleDraft: Draft,
  schedule: Schedule,
): BundleEntry<Slot>[] {
  const res: BundleEntry<Slot>[] = []
  const start = new Date(startDate)
  const end = new Date(endDate)
  const stepMinutes = 30 // e.g. 30 minutes per slot
  // normalize to local midnight
  start.setHours(0, 0, 0, 0)
  end.setHours(0, 0, 0, 0)

  const DOW: DayKey[] = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

  for (const d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    const key = DOW[d.getDay()]
    const block = scheduleDraft[key]
    if (!block) continue

    const { h: sh, m: sm } = parseHHMM(block.start)
    const { h: eh, m: em } = parseHHMM(block.end)

    let slotStart = new Date(d.getFullYear(), d.getMonth(), d.getDate(), sh, sm, 0, 0)
    const blockEnd = new Date(d.getFullYear(), d.getMonth(), d.getDate(), eh, em, 0, 0)

    while (slotStart < blockEnd) {
      const slotEnd = new Date(slotStart.getTime() + stepMinutes * 60000)
      if (slotEnd > blockEnd) break
      const slot: Slot = {
        resourceType: 'Slot',
        start: slotStart.toISOString(),
        end: slotEnd.toISOString(),
        status: 'free',
        schedule: createReference(schedule),
      }
      res.push(createEntry<Slot>(slot))
      slotStart = slotEnd
    }
  }
  return res
}

async function findOverlappingSlots({
  timeSlots,
  schedule,
  medplum,
}: {
  timeSlots: Array<{ slotStart: string; slotEnd: string }>
  schedule: Schedule
  medplum: MedplumClient
}): Promise<BundleEntry[]> {
  // Perform lookup to find free slots that already set up, to remove them and create new
  const overlapingSlotsRequestEntries: BundleEntry<Slot>[] = timeSlots.map((slot) => {
    const formattedTimeSet = {
      start: slot.slotStart,
      end: slot.slotEnd,
    }
    return {
      request: {
        method: 'GET',
        url: `Slot?schedule=Schedule/${schedule.id}&status=free,busy-unavailable,busy&start=ge${formattedTimeSet.start}&end=le${formattedTimeSet.end}`,
      },
      resourceType: 'Slot',
    }
  })
  const overlappingSlotsBundleRes = await medplum.executeBatch({
    resourceType: 'Bundle',
    type: 'batch',
    entry: overlapingSlotsRequestEntries,
  })
  const overlappingFreeSlots = [...(overlappingSlotsBundleRes.entry || [])]
    .map((per) =>
      (per.resource as any)?.type !== 'searchset' ? [per] : (per.resource as any)?.entry || [],
    )
    .flat()
  return overlappingFreeSlots
}

export async function createBlockedSlots({
  medplum,
  schedule,
  blockedPeriods,
}: {
  medplum: MedplumClient
  schedule: Schedule
  blockedPeriods: Array<{
    startDate: string | Date
    endDate: string | Date
    startTime: string
    endTime: string
  }>
}) {
  const timeSlotSet = [] as Array<{ start: string; end: string }>
  blockedPeriods.forEach((bp) => {
    const isMultipleDay = isMultipleDayRange(bp.startDate as string, bp.endDate as string)
    if (isMultipleDay) {
      const dailyPairs = buildDailyPairs(
        updateDateTime24(bp.startDate, bp.startTime),
        updateDateTime24(bp.endDate, bp.endTime),
      )
      timeSlotSet.push(...dailyPairs)
    } else {
      timeSlotSet.push({
        start: updateDateTime24(bp.startDate, bp.startTime),
        end: updateDateTime24(bp.endDate, bp.endTime),
      })
    }
  })
  const uniqSlots = [...new Map(timeSlotSet.map((o) => [JSON.stringify(o), o])).values()] // remove duplicates in case user select periods that duplicates timeslots
  return await Promise.all(
    uniqSlots.map(({ start, end }) =>
      createSingleSlot({
        schedule,
        medplum,
        status: 'busy-unavailable',
        start,
        end,
      }),
    ),
  )
}

export async function createFreeSlotsForSchedule({
  medplum,
  schedule,
  formData,
}: {
  medplum: MedplumClient
  schedule: Schedule
  formData: { scheduleDraft: Draft; endsOn: Date | null }
}): Promise<BundleEntryResponse[] | Error> {
  try {
    // Define time range for free slots and transform it to ISO format    ? toIsoLocalZ(new Date(formData.endsOn).setHours(23, 59, 59, 999))
    const startOnV2 = toIsoLocalZ(new Date())
    const endOn = formData.endsOn
      ? toIsoLocalZ(new Date(new Date(formData.endsOn).setHours(20)))
      : toIsoLocalZ(new Date(Date.now() + 2 * 30 * 24 * 60 * 60 * 1000)) // 2 weeks default
    // Build free slots for selected time period and draft schedule
    const freeSlots = buildFreeSlots(startOnV2, endOn, formData.scheduleDraft, schedule)
    let freeSlotsToCreate = freeSlots
    //Check does existing blocked slots overlap with slots we going set available for appointments

    const existingBookedSlots = await medplum.searchResources(
      'Slot',
      `schedule=Schedule/${schedule.id}&start=ge${startOnV2}&end=lt${endOn}&_count=10000&status=busy-unavailable,busy`,
    )
    //Filter out slots that are overlapping with slots that were defined as busy-unavailable
    if (existingBookedSlots && existingBookedSlots.length > 0) {
      const blockedslotsTimeSet = new Set([...existingBookedSlots]?.map((slot) => slot.start))
      freeSlotsToCreate = freeSlots.filter((slot) => {
        return slot?.resource?.start && !blockedslotsTimeSet.has(slot.resource.start)
      })
    }
    // Create free slots in batch
    const freeSlotsCreationRes = await medplum.executeBatch({
      resourceType: 'Bundle',
      type: 'batch',
      entry: freeSlotsToCreate,
    })
    if (freeSlotsCreationRes) {
      console.log(
        `Created ${freeSlotsCreationRes.entry?.length} free slots for schedule ${schedule.id}`,
      )
    }
    medplum.invalidateSearches('Slot')
    return freeSlotsCreationRes.entry as BundleEntryResponse[]
  } catch (err) {
    return err as Error
  }
}

export async function deleteFreeSlotsTodayUTC(
  medplum: MedplumClient,
  scheduleId: string,
  dayISO = utcDayIso(),
) {
  const deletedSlotsResult: Slot[] = []
  try {
    // Fetch existing slots for the schedule on the specified day
    const start = dayISO
    const existingSlots = await medplum.searchResources(
      'Slot',
      `schedule=Schedule/${scheduleId}&start=ge${start}&status=free&_count=10000`,
    )
    if (!existingSlots || existingSlots.length === 0) {
      // console.log(`No existing slots found for schedule ${scheduleId} on ${dayISO}`);
      return { deleted: [], deletedStarts: [] }
    }
    // Delete each free slot found
    const deletedSlotsResult = await medplum.executeBatch({
      resourceType: 'Bundle',
      type: 'transaction',
      entry: existingSlots.map((slot) => ({
        request: {
          method: 'DELETE',
          url: `Slot/${encodeURIComponent(slot.id)}`,
        },
      })),
    })
    return {
      deleted: deletedSlotsResult,
      // Returned so the caller can tell the Zocdoc sync which days lost slots — a delete never
      // reaches it on its own.
      deletedStarts: existingSlots.flatMap((slot) => (slot.start ? [slot.start] : [])),
    }
  } catch (error) {
    // Handle errors (e.g., network issues, Medplum API errors)
    return {
      deleted: deletedSlotsResult,
      deletedStarts: [],
      error: error instanceof Error ? error.message : 'Unknown error',
    }
  }
}
export async function createSingleSlot({
  medplum,
  schedule,
  start,
  end,
  status,
}: {
  medplum: MedplumClient
  schedule: Schedule
  start: Date | string
  end: Date | string
  status: string
}): Promise<Resource | Error> {
  // Define ISO strings for slot start and end
  const slotStartISO = new Date(start).toISOString()
  const slotEndISO = new Date(end).toISOString()

  const slots = createTimeSlots(slotStartISO, slotEndISO)
  try {
    const overlappingSlots = await findOverlappingSlots({ timeSlots: slots, schedule, medplum })
    const busySlots = [
      ...new Set(
        overlappingSlots
          .filter(({ resource: slot }) => (slot as Slot)?.status === 'busy')
          .map(({ resource: slot }) => (slot as Slot)?.start),
      ),
    ]

    if (overlappingSlots.length > 0) {
      const slotsIdsToDelete = overlappingSlots
        .filter(({ resource: slot }) => (slot as Slot).status !== 'busy')
        .map(({ resource: slot }) => (slot as Slot).id as string)
      deleteSlotsByIds(medplum, slotsIdsToDelete)
    }
    const newSlotsEntries = slots
      .filter((timeFramesSlot) => !busySlots.includes(timeFramesSlot.slotStart))
      .map((timeFramesSlot) => {
        const slot: Slot = {
          resourceType: 'Slot',
          schedule: createReference(schedule),
          status: status as SlotStatus,
          start: timeFramesSlot.slotStart,
          end: timeFramesSlot.slotEnd,
        }
        return createEntry<Slot>(slot)
      })
    const res = await medplum.executeBatch({
      resourceType: 'Bundle',
      type: 'batch',
      entry: newSlotsEntries,
    })
    medplum.invalidateSearches('Slot')
    return res
  } catch (error) {
    logError('schedule', 'Failed to create slots', error)
    throw error
  }
}

type BookAppointmentResult = {
  success: boolean
  message?: string
}

export const createAppointment = async ({
  patientId,
  start: startTime,
  end: endTime,
  reasonId,
  medplum,
  middleware,
  schedule,
}: {
  patientId?: string | null
  start: string
  end: string
  /** Reason-for-visit catalog id, when staff picked one. */
  reasonId?: string | null
  medplum: MedplumClient
  middleware: ApiClient
  schedule: Schedule
}): Promise<void> => {
  if (!patientId || !startTime || !endTime) {
    return
  }

  try {
    const start = new Date(startTime)
    const startDate = start.toISOString()

    if (start < new Date()) {
      showNotification({
        color: 'red',
        message: 'Cannot create appointments in the past',
      })
      return
    }

    const slots = await medplum.searchResources(
      'Slot',
      `schedule=Schedule/${schedule?.id}&status=free&start=${startDate}&_count=1`,
    )
    const neededSlot = slots[0]
    if (!neededSlot?.id) {
      showNotification({
        color: 'red',
        message: 'No available slots',
      })
      return
    }

    const result = (await middleware.post('/appointment/book', {
      patientId,
      slot: neededSlot,
      // Middleware turns this into Appointment/Encounter reasonCode[0]. The
      // explicit 'staff' source is what keeps the stored provenance honest —
      // omitting it makes middleware record the reason as patient-chosen.
      ...(reasonId ? { reason: { id: reasonId, source: VISIT_REASON_SOURCE_STAFF } } : {}),
    })) as BookAppointmentResult

    if (result?.success) {
      showNotification({
        color: 'green',
        message: 'Appointment created successfully',
      })
    } else {
      showNotification({
        color: 'red',
        message: result?.message || 'Error creating appointment',
      })
    }
  } catch (error) {
    logError('schedule', 'Failed to create appointment', error)
    // The middleware client throws the response body, so a 400's message is the reason
    // (e.g. State Match: "Dr. X is not licensed in the patient's state (TX).").
    const message = (error as { message?: unknown })?.message
    showNotification({
      color: 'red',
      message: typeof message === 'string' && message ? message : 'Error creating appointment',
    })
  }
}
export const cancelAppointment = async ({
  middleware,
  id,
  cancelZocdoc = false,
}: {
  middleware: ApiClient
  id: string
  /** Also cancel the bound Zocdoc booking — the dialog's checkbox, for a Zocdoc-bound visit. */
  cancelZocdoc?: boolean
}): Promise<boolean> => {
  try {
    const result = (await middleware.delete(
      '/appointment/{encounterId}',
      { encounterId: id, ...(cancelZocdoc && { cancelZocdoc: 'true' }) },
      // The third argument is what makes `cancelZocdoc` a QUERY parameter. On a non-GET the
      // fetcher puts anything outside the path template into the BODY, and `@Query` never sees
      // it — so without this the flag is silently dropped and the Zocdoc booking stays live.
      { cancelZocdoc: true },
    )) as {
      success: boolean
      message?: string
      zocdocCancelled?: boolean
      zocdocMessage?: string
    }

    if (result?.success) {
      // Ours is cancelled either way. A Zocdoc half that did not go through is said out loud
      // rather than folded into the success message: the booking is still live there, and only a
      // person can finish it.
      if (result.zocdocCancelled === false) {
        showNotification({
          color: 'yellow',
          title: 'Cancelled here, but not on Zocdoc',
          message: result.zocdocMessage ?? 'The Zocdoc booking is still live — cancel it there.',
          autoClose: false,
        })
        return true
      }
      showNotification({
        color: 'green',
        message: result.zocdocCancelled
          ? 'Appointment cancelled here and on Zocdoc'
          : 'Appointment removed successfully',
      })
      return true
    } else {
      showNotification({
        color: 'red',
        message: result?.message || 'Error removing appointment',
      })
      return false
    }
  } catch (error) {
    logError('schedule', 'Failed to cancel appointment', error)
    // The server's own message when it has one — a Zocdoc-bound visit refused to a caller without
    // the coordinator role says who to ask, and "Error removing appointment" would bury that.
    // `ApiClient` throws the response body itself, so the message sits on the thrown value.
    const message = (error as { message?: string } | undefined)?.message
    showNotification({
      color: 'red',
      message: typeof message === 'string' && message ? message : 'Error removing appointment',
    })
    return false
  }
}
